/**
 * Persistent strip rendered above the main app chrome whenever an
 * admin is acting-as another user (consultant, client, or facility
 * manager). The most important UX element of the impersonation
 * feature — without it, an admin can forget which tenant their next
 * click will write into.
 *
 * Click "Exit" → calls ``stopActingAs`` (clears local state + closes
 * the server-side audit row) and navigates back to ``/admin``.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useToast } from '../contexts/ToastContext';

const ImpersonationBanner: React.FC = () => {
  const { impersonation, stopActingAs } = useImpersonation();
  const { showError } = useToast();
  const navigate = useNavigate();
  const [exiting, setExiting] = useState(false);

  if (!impersonation) return null;

  const handleExit = async () => {
    if (exiting) return;
    setExiting(true);
    try {
      await stopActingAs();
      navigate('/admin');
    } catch (err) {
      showError(
        'Could not exit impersonation',
        err instanceof Error ? err.message : 'Please try again.',
      );
    } finally {
      setExiting(false);
    }
  };

  return (
    <div
      role="status"
      style={{
        background: '#fff3cd',
        color: '#664d03',
        borderBottom: '1px solid #ffe69c',
        padding: '0.5rem 1rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: '0.875rem',
      }}
    >
      <span>
        Acting as <strong>{impersonation.actAsUserName}</strong>
        {' · '}
        {impersonation.actAsContextLabel}
        {' · Preview: actions run with your admin permissions'}
      </span>
      <button
        type="button"
        onClick={handleExit}
        disabled={exiting}
        className="btn btn-sm"
        style={{
          background: '#fff',
          color: '#664d03',
          border: '1px solid #ffe69c',
        }}
      >
        {exiting ? 'Exiting…' : 'Exit'}
      </button>
    </div>
  );
};

export default ImpersonationBanner;
